/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx; this change only swaps an icon import, converting to .tsx is a separate migration */
import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
} from '@chakra-ui/react';
import { isValidElement, useRef } from 'react';
import { useEnableHeaderFooterBorder } from '@/hooks';
import { KText } from '@/ui-library/components/text';
import { X } from '@kula-ai/icons-core';

export const TableActionModal = (props) => {
  const {
    modalActions,
    selected = [],
    unSelected = [],
    selectedAction = {},
    toggleRowSelected,
    toggleAllRowsSelected,
    resetSelectionValue,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onClose } = modalActions;

  const {
    label,
    title = '',
    description = '',
    successButtonText = 'Add',
    closeButtonText = 'Cancel',
    isButtonDisabled = false,
    isActionLoading,
    RenderContent,
    onActionSubmit = () => {},
    showCandidateInfo = true,
    modalHeaderProps,
    modalFooterProps,
    isCustomModal = false,
  } = selectedAction;

  const renderUserList = (currentUser) => {
    const { contact, tableId } = currentUser || {};
    const { name, id, avatar, email } = contact || {};
    return (
      <Flex key={id} padding="6px 0" alignItems="center">
        <Avatar
          src={avatar}
          name={name}
          width="40px"
          height="40px"
          borderRadius="12px"
        />
        <Flex flexDir="column" ml="12px" flex="1">
          <KText>{name}</KText>
          <KText>{email}</KText>
        </Flex>
        <Flex
          onClick={() => {
            toggleRowSelected(tableId, false);
          }}
          cursor="pointer"
        >
          <Tooltip variant="default" label={`Clear ${name} from selection`}>
            <Flex>
              <KText mr="3px" textAlign="center">
                Remove
              </KText>
              <Icon as={X} w="20px" h="20px" />
            </Flex>
          </Tooltip>
        </Flex>
      </Flex>
    );
  };

  const renderCandidatesInfo = () => {
    return (
      <Box m="15px 0">
        <KText>
          Candidates to {label} {`(${selected?.length})`}
        </KText>
        <Box mt="5px" maxH="250px" overflow="auto" gap="5px">
          {selected.map(renderUserList)}
        </Box>
      </Box>
    );
  };

  const handleOnSuccessClose = () => {
    onClose();
    toggleAllRowsSelected(false);
    resetSelectionValue();
  };

  const handleLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const customProps = { ...props, apiType: 'bulk', modalActions: modalActions };

  const content = isValidElement(RenderContent) ? (
    <RenderContent {...customProps} />
  ) : (
    typeof RenderContent === 'function' && RenderContent(customProps)
  );

  const modalBodyRef = useRef(null);
  const { hasOverflow } = useEnableHeaderFooterBorder(modalBodyRef);

  const renderModal = () => {
    return (
      <>
        <Modal
          isCentered
          isOpen={isOpen}
          onClose={onClose}
          autoFocus={false}
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader
              pb="0em"
              borderBottom={hasOverflow ? '1px solid ' : 'none'}
              borderColor={hasOverflow ? 'modal-header-footer-border' : 'none'}
              {...modalHeaderProps}
            >
              <Flex flexDir="column" gap=".5em">
                <KText variant="heading_2">{title || label}</KText>
                {description && <KText>{description}</KText>}
              </Flex>
            </ModalHeader>
            <ModalBody overflow={'scroll'} ref={modalBodyRef}>
              {content}
              {showCandidateInfo && renderCandidatesInfo()}
            </ModalBody>
            <ModalFooter
              borderTop={hasOverflow ? '1px solid ' : 'none'}
              borderColor={hasOverflow ? 'modal-header-footer-border' : 'none'}
              {...modalFooterProps}
            >
              <Button
                variant="secondary"
                mr={3}
                onClick={onClose}
                data-test-id="table-action-modal-secondary-cta-button"
              >
                {closeButtonText}
              </Button>
              <Button
                onClick={() => {
                  onActionSubmit(
                    selected,
                    handleOnSuccessClose,
                    handleLoading,
                    unSelected
                  );
                }}
                disabled={isButtonDisabled || isActionLoading || isLoading}
                isLoading={isActionLoading || isLoading}
                variant="primary"
                data-test-id="table-action-modal-primary-cta-button"
              >
                {successButtonText}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  };

  return <Box>{isOpen && isCustomModal ? content : renderModal()}</Box>;
};
